import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { useTranslation } from 'react-i18next';

interface Props {
  portions: number;
  fish: number;
  veg: number;
  meat: number;
  onPortionsChange: (n: number) => void;
  onFishChange: (n: number) => void;
  onVegChange: (n: number) => void;
  onMeatChange: (n: number) => void;
}

function StepControl({ value, onChange, min = 0, max = 14 }: { value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <View style={styles.stepperRow}>
      <Pressable
        style={[styles.stepBtn, value <= min && styles.stepBtnDisabled]}
        onPress={() => onChange(Math.max(min, value - 1))}
      >
        <Text style={styles.stepBtnText}>−</Text>
      </Pressable>
      <Text style={styles.stepValue}>{value}</Text>
      <Pressable
        style={[styles.stepBtn, value >= max && styles.stepBtnDisabled]}
        onPress={() => onChange(Math.min(max, value + 1))}
      >
        <Text style={styles.stepBtnText}>+</Text>
      </Pressable>
    </View>
  );
}

function TargetStepper({ role, label, value, onChange }: { role: DietaryRole; label: string; value: number; onChange: (n: number) => void }) {
  return (
    <View style={styles.targetItem}>
      <DietDot role={role} size={7} />
      <Text style={styles.targetLabel}>{label}</Text>
      <StepControl value={value} onChange={onChange} />
    </View>
  );
}

export function WeekBar({ portions, fish, veg, meat, onPortionsChange, onFishChange, onVegChange, onMeatChange }: Props) {
  const { t } = useTranslation();
  return (
    <View style={styles.bar}>
      {/* Default portions */}
      <View style={styles.group}>
        <Feather name="users" size={15} color={Paprika.textMuted} />
        <Text style={styles.label}>{t('mealConfig.defaultPortions')}</Text>
        <StepControl value={portions} onChange={onPortionsChange} min={1} max={12} />
      </View>

      <View style={styles.separator} />

      {/* Dietary distribution targets */}
      <View style={styles.group}>
        <Text style={styles.label}>{t('mealConfig.targetMix')}</Text>
        <TargetStepper role="fish" label={t('mealConfig.fish')} value={fish} onChange={onFishChange} />
        <TargetStepper role="vegetarian" label={t('mealConfig.veggie')} value={veg} onChange={onVegChange} />
        <TargetStepper role="meat" label={t('mealConfig.meat')} value={meat} onChange={onMeatChange} />
      </View>

      {/* Soft-guide note, pushed to the far right */}
      <Text style={styles.guideNote}>{t('mealConfig.softGuide')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Paprika.border,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginHorizontal: 24,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    rowGap: 10,
  } as any,
  group: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  label: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  separator: {
    width: 1,
    height: 28,
    backgroundColor: Paprika.border,
  },
  targetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  targetLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  guideNote: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    marginLeft: 'auto',
  } as any,
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  stepBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepBtnDisabled: {
    opacity: 0.35,
  },
  stepBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    color: Paprika.textPrimary,
    lineHeight: 18,
  },
  stepValue: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20,
    color: Paprika.textPrimary,
    minWidth: 22,
    textAlign: 'center',
  },
});
